import { createApiClient, type ApiClient } from '@luacentrix/api-client';
import { isApiError, normalizeLanguage, type ApiLanguage } from '@luacentrix/shared';

export const LANGUAGE_STORAGE_KEY = 'luacentrix.language';

export const SESSION_TOKEN_STORAGE_KEY = 'luacentrix.session_token';

export interface DashboardEnv {
  apiBaseUrl: string;
  requireHttps: boolean;
}

export interface ErrorPresentation {
  code: string;
  message: string;
  status: number | null;
  field: string | null;
}

function firstString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function resolveEnv(source: Record<string, unknown> = {}): DashboardEnv {
  const apiBaseUrl = firstString(source['VITE_API_BASE_URL']) ?? window.location.origin;
  return {
    apiBaseUrl,
    requireHttps:
      !apiBaseUrl.startsWith('http://localhost') && !apiBaseUrl.startsWith('http://127.0.0.1'),
  };
}

function storedLanguage(): string | null {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storedSessionToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function readLanguage(preferred?: string | null): ApiLanguage {
  if (preferred !== undefined) return normalizeLanguage(preferred);
  const stored = storedLanguage();
  if (stored !== null) return normalizeLanguage(stored);
  return normalizeLanguage(navigator.languages?.[0] ?? navigator.language);
}

export function writeLanguage(language: ApiLanguage): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    return;
  }
}

export function createApi(
  env: DashboardEnv = resolveEnv(),
  language: ApiLanguage = readLanguage(),
): ApiClient {
  return createApiClient({
    baseUrl: env.apiBaseUrl,
    accessToken: () => storedSessionToken(),
    language,
    credentials: 'include',
    requireHttps: env.requireHttps,
    timeoutMs: 15_000,
  });
}

export function describeError(
  reason: unknown,
  language: ApiLanguage = readLanguage(),
): ErrorPresentation {
  if (isApiError(reason)) {
    const localized = reason.withLanguage(language);
    const details = localized.details;
    return {
      code: localized.code,
      message: localized.message,
      status: localized.status,
      field: firstString(details?.field) ?? null,
    };
  }
  if (reason instanceof Error) {
    return { code: 'UNKNOWN_ERROR', message: reason.message, status: null, field: null };
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred.',
    status: null,
    field: null,
  };
}
